/**
 * Liquid-fill button hover — global.
 *
 * Plain script (no bundler). Loaded from theme.liquid on EVERY page after the
 * GSAP libs. Progressively enhances boxed buttons and survey answer blocks so
 * that, on hover, the ink colour rises from the bottom like liquid — a subtle,
 * organic wave that fills the box and inverts the text where the waterline
 * passes over it.
 *
 * Technique: inject an absolutely-positioned overlay (`.liquid-reveal`) that is
 * a duplicate of the label in the *surface* colour on an *ink*-coloured
 * background, then clip it to a wavy waterline via `clip-path: polygon()`
 * recomputed each frame. Above the waterline the overlay is clipped away so the
 * host's normal text shows; below it, the filled/inverted copy shows — giving a
 * crisp per-glyph inversion at the moving surface.
 *
 * Colours come from two CSS custom properties set per-selector in
 * liquid-button.css: `--fill` (the ink/liquid) and `--fill-text` (the text once
 * submerged). Wave params are randomised per element so no two buttons match.
 *
 * Respects prefers-reduced-motion and the theme editor: when either is on we
 * don't enhance at all, leaving the existing instant-invert CSS in place.
 */
(function () {
  if (window.__tamreLiquidInit) return;
  window.__tamreLiquidInit = true;

  var SELECTOR = '.btn, .interlude__btn, .survey__choice, .survey__submit, .survey__thanks-button';
  var SAMPLES = 40; // wave points across the top edge — high, so the curve reads smooth
  var TAU = Math.PI * 2;
  // Seconds for a full bottom→top rise. Kept slow so the liquid climbs
  // gradually and then settles into a completely filled box.
  var RISE_TIME = 2.1;

  function isStaticMode() {
    return (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !!(window.Shopify && window.Shopify.designMode)
    );
  }

  function ready() {
    return !!window.gsap;
  }
  function whenReady(cb) {
    if (ready()) return cb();
    var tries = 0;
    (function poll() {
      if (ready()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function labelText(el) {
    var span = el.querySelector('.survey__choice-label');
    return (span ? span.textContent : el.textContent || '').trim();
  }

  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  function enhance(el) {
    if (!el || el.__liquidInit) return;
    el.__liquidInit = true;
    el.classList.add('is-liquid');

    // Duplicate label copy that shows the "submerged" (inverted) text.
    var reveal = document.createElement('span');
    reveal.className = 'liquid-reveal';
    reveal.setAttribute('aria-hidden', 'true');
    reveal.textContent = labelText(el);
    el.appendChild(reveal);

    // Independent phases so the surface never repeats the same shape twice.
    var state = {
      level: 0,
      travel: rand(0, TAU), // horizontal drift of the ripple
      slosh: rand(0, TAU),  // side-to-side tilt ("bounce back and forth")
      bob: rand(0, TAU)     // gentle vertical settle
    };
    // Wave character, randomised per element and expressed as fractions of the
    // button height so short and tall boxes slosh proportionally. Two ripples
    // travelling opposite ways read as liquid rather than a sine curve.
    var amp1 = rand(0.09, 0.15);   // primary ripple height
    var amp2 = rand(0.04, 0.08);   // secondary counter-ripple
    var waves1 = rand(1.1, 1.9);   // crests across the width
    var waves2 = rand(2.2, 3.4);
    var tiltAmp = rand(0.14, 0.24); // how far the surface tips side to side
    var bobAmp = rand(0.05, 0.09);
    var travelSpeed = rand(2.0, 3.4) * (Math.random() < 0.5 ? -1 : 1);
    var sloshSpeed = rand(1.6, 2.6);
    var bobSpeed = rand(1.0, 1.8);

    var rafId = 0;
    var last = 0;
    var tween = null;
    var locked = false; // survey answer selected → hold full
    var w = 0;
    var h = 0; // dimensions cached per animation (constant during a tween)

    function draw() {
      // Fully submerged: solid box (a locked/selected answer must be totally
      // filled, with no wavy troughs left uncovered at the very top).
      if (state.level >= 0.999) {
        reveal.style.clipPath = 'inset(0)';
        return;
      }
      // Envelope: the wave emerges flat from the bottom, peaks in the middle of
      // the climb, then flattens again as it tops out — so it settles into a
      // completely filled box with no crease, and never spikes at the extremes.
      var env = Math.sin(Math.max(0, Math.min(1, state.level)) * Math.PI);
      var tilt = tiltAmp * h * env * Math.sin(state.slosh); // + one side, - the other
      var base = (1 - state.level) * h + bobAmp * h * env * Math.sin(state.bob);
      var pts = '0px ' + h.toFixed(1) + 'px';
      for (var i = 0; i <= SAMPLES; i++) {
        var t = i / SAMPLES;
        var x = t * w;
        var y =
          base +
          amp1 * h * env * Math.sin(t * waves1 * TAU + state.travel) +
          amp2 * h * env * Math.sin(t * waves2 * TAU - state.travel * 1.4) +
          tilt * (t - 0.5) * 2; // raises one edge, drops the other, alternating
        pts += ', ' + x.toFixed(1) + 'px ' + y.toFixed(1) + 'px';
      }
      pts += ', ' + w.toFixed(1) + 'px ' + h.toFixed(1) + 'px';
      reveal.style.clipPath = 'polygon(' + pts + ')';
    }

    // Render loop — runs the whole time the surface is showing (through the
    // rise AND while hovered), so the liquid keeps sloshing instead of freezing.
    function loop(now) {
      if (!last) last = now;
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      state.travel += travelSpeed * dt;
      state.slosh += sloshSpeed * dt;
      state.bob += bobSpeed * dt;
      draw();
      rafId = requestAnimationFrame(loop);
    }
    function startLoop() {
      if (rafId) return;
      last = 0;
      w = el.offsetWidth;
      h = el.offsetHeight;
      draw(); // paint the current waterline before showing, so there's no flash
      reveal.style.visibility = 'visible';
      rafId = requestAnimationFrame(loop);
    }
    function stopLoop() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    }

    // Rise all the way to the top and settle into a solid fill. Duration scales
    // with the distance left to climb, so it's a slow ~2s rise from empty but a
    // quick top-up if a survey answer is clicked when already part-filled.
    function fillUp() {
      if (tween) tween.kill();
      startLoop();
      var duration = Math.max(0.35, (1 - state.level) * RISE_TIME);
      tween = gsap.to(state, {
        level: 1,
        duration: duration,
        ease: 'power1.inOut', // gentle in and out — no hard start/stop
        onComplete: function () {
          draw(); // solid fill
          stopLoop();
        }
      });
    }
    function recede() {
      if (locked) return;
      if (tween) tween.kill();
      startLoop();
      tween = gsap.to(state, {
        level: 0,
        duration: Math.max(0.3, state.level * 0.7),
        ease: 'power2.in',
        onComplete: function () {
          stopLoop();
          reveal.style.visibility = 'hidden';
        }
      });
    }

    el.addEventListener('mouseenter', function () {
      if (locked) return;
      fillUp();
    });
    el.addEventListener('mouseleave', recede);

    // Survey answers: clicking locks the fill solid at full while the option
    // stays selected; unticking releases it.
    var box = el.querySelector('.survey__choice-input');
    if (box) {
      box.addEventListener('change', function () {
        if (box.checked) {
          locked = true;
          fillUp();
        } else {
          locked = false;
          recede();
        }
      });
    }
  }

  function enhanceAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(enhance);
  }

  if (isStaticMode()) return; // leave the instant-invert CSS untouched

  whenReady(function () {
    enhanceAll();
  });

  // Theme editor: enhance sections added/reloaded after first paint. The
  // per-element __liquidInit guard prevents double overlays.
  document.addEventListener('shopify:section:load', function (e) {
    if (isStaticMode() || !ready()) return;
    enhanceAll(e.target);
  });
})();
